import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar } from 'lucide-react';

const WEDDING_DATE = new Date('2026-10-18T17:30:00');

const programSteps = [
  {
    icon: 'https://customer-assets.emergentagent.com/job_4ebaa9cc-440b-4527-9761-4d4c07f094d6/artifacts/jeb66474_Icone%20Ce%CC%81re%CC%81monie.png',
    time: '17h30',
    title: 'Cérémonie',
    highlight: true,
  },
  {
    icon: 'https://customer-assets.emergentagent.com/job_4ebaa9cc-440b-4527-9761-4d4c07f094d6/artifacts/udjspgm1_Icone%20Cocktail.png',
    time: null,
    title: 'Cocktail',
    highlight: false,
  },
  {
    icon: 'https://customer-assets.emergentagent.com/job_4ebaa9cc-440b-4527-9761-4d4c07f094d6/artifacts/c9e2h1bf_Icone%20Diner.png',
    time: null,
    title: 'Dîner',
    highlight: false,
  },
  {
    icon: 'https://customer-assets.emergentagent.com/job_4ebaa9cc-440b-4527-9761-4d4c07f094d6/artifacts/kwqar5dq_Icone%20Soire%CC%81e.png',
    time: null,
    title: 'Soirée',
    highlight: false,
  },
];

const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

const CountdownUnit = ({ value, label }) => (
  <div className="flex flex-col items-center" data-testid={`countdown-${label.toLowerCase()}`}>
    <span className="font-display text-2xl md:text-3xl text-[#e17c3b]">{String(value).padStart(2, '0')}</span>
    <span className="text-[10px] md:text-xs tracking-widest uppercase text-[#8d5f3f]/60 mt-1">{label}</span>
  </div>
);

export const HomePage = () => {
  const timeLeft = useCountdown(WEDDING_DATE);

  return (
    <div className="min-h-screen flex flex-col" data-testid="home-page">
      {/* MAIN FOCUS: Programme de la journée (now at the top) */}
      <section
        className="pt-28 md:pt-32 pb-16 md:pb-24 px-6 md:px-12 bg-gradient-to-b from-white via-[#fcf6f1]/40 to-white"
        data-testid="home-programme-section"
      >
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-10 md:mb-14">
            <p className="text-sm tracking-[0.3em] uppercase text-[#8d5f3f]/70 mb-5 animate-fade-in">
              Dimanche 18 octobre 2026
            </p>

            {/* Compact Countdown — right below the date */}
            <div
              className="inline-flex items-center gap-3 md:gap-5 bg-[#fcf6f1] border border-[#e8ddd4] rounded-full px-6 md:px-8 py-3 md:py-4 shadow-sm mb-8 animate-fade-in delay-75"
              data-testid="countdown"
            >
              <CountdownUnit value={timeLeft.days} label="Jours" />
              <span className="font-display text-xl md:text-2xl text-[#8d5f3f]/30">:</span>
              <CountdownUnit value={timeLeft.hours} label="Heures" />
              <span className="font-display text-xl md:text-2xl text-[#8d5f3f]/30">:</span>
              <CountdownUnit value={timeLeft.minutes} label="Minutes" />
              <span className="font-display text-xl md:text-2xl text-[#8d5f3f]/30">:</span>
              <CountdownUnit value={timeLeft.seconds} label="Secondes" />
            </div>

            <h2
              className="font-display text-4xl md:text-5xl text-[#e17c3b] mb-4 animate-slide-up"
              data-testid="home-programme-title"
            >
              Le déroulement de la journée
            </h2>
            <div className="flex items-center justify-center gap-3 animate-fade-in delay-100">
              <span className="w-12 h-px bg-[#e17c3b]/40" />
              <span className="font-accent italic text-lg text-[#8d5f3f]/70">Hacienda El Vizir</span>
              <span className="w-12 h-px bg-[#e17c3b]/40" />
            </div>
          </div>

          {/* Desktop Timeline */}
          <div className="hidden md:block relative">
            {/* Connecting Line */}
            <div className="absolute top-16 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-[#8d5f3f]/20 via-[#8d5f3f] to-[#8d5f3f]/20" />

            <div className="grid grid-cols-4 gap-6">
              {programSteps.map((step, index) => (
                <div
                  key={step.title}
                  className="flex flex-col items-center text-center animate-slide-up"
                  style={{ animationDelay: `${index * 150}ms` }}
                  data-testid={`home-programme-step-${index}`}
                >
                  {/* Icon Circle */}
                  <div
                    className={`relative z-10 w-32 h-32 rounded-full flex items-center justify-center mb-6 transition-all duration-500 ${
                      step.highlight
                        ? 'bg-[#e17c3b] border-4 border-[#e17c3b] shadow-xl shadow-[#e17c3b]/20 scale-110'
                        : 'bg-white border-2 border-[#8d5f3f] shadow-md'
                    }`}
                  >
                    <img
                      src={step.icon}
                      alt={step.title}
                      className={`w-16 h-16 object-contain ${step.highlight ? 'brightness-0 invert' : ''}`}
                    />
                  </div>

                  {/* Time (only for Cérémonie) */}
                  <div className="h-10 flex items-center justify-center">
                    {step.time ? (
                      <p
                        className="font-display text-3xl text-[#e17c3b] font-semibold"
                        data-testid={`home-programme-time-${index}`}
                      >
                        {step.time}
                      </p>
                    ) : (
                      <span className="w-6 h-px bg-[#8d5f3f]/30" />
                    )}
                  </div>

                  {/* Title */}
                  <h3
                    className={`font-display mt-2 ${
                      step.highlight ? 'text-2xl text-[#8d5f3f] font-semibold' : 'text-xl text-[#8d5f3f]'
                    }`}
                  >
                    {step.title}
                  </h3>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Vertical Timeline */}
          <div className="md:hidden relative">
            {/* Vertical connecting line */}
            <div className="absolute left-10 top-10 bottom-10 w-0.5 bg-gradient-to-b from-[#8d5f3f]/20 via-[#8d5f3f] to-[#8d5f3f]/20" />

            <div className="space-y-8">
              {programSteps.map((step, index) => (
                <div
                  key={step.title}
                  className="flex items-center gap-5 animate-slide-up relative"
                  style={{ animationDelay: `${index * 120}ms` }}
                  data-testid={`home-programme-step-mobile-${index}`}
                >
                  {/* Icon */}
                  <div
                    className={`w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 transition-all ${
                      step.highlight
                        ? 'bg-[#e17c3b] border-4 border-[#e17c3b] shadow-xl shadow-[#e17c3b]/20'
                        : 'bg-white border-2 border-[#8d5f3f] shadow-md'
                    }`}
                  >
                    <img
                      src={step.icon}
                      alt={step.title}
                      className={`w-10 h-10 object-contain ${step.highlight ? 'brightness-0 invert' : ''}`}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    {step.time ? (
                      <p
                        className="font-display text-2xl text-[#e17c3b] font-semibold leading-tight"
                        data-testid={`home-programme-time-mobile-${index}`}
                      >
                        {step.time}
                      </p>
                    ) : (
                      <span className="block w-5 h-px bg-[#8d5f3f]/30 mb-1" />
                    )}
                    <h3
                      className={`font-display text-[#8d5f3f] ${
                        step.highlight ? 'text-2xl font-semibold' : 'text-xl'
                      }`}
                    >
                      {step.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Link to detailed page */}
          <div className="text-center mt-14">
            <Link
              to="/mariage"
              className="inline-flex items-center gap-2 text-[#8d5f3f]/80 hover:text-[#e17c3b] transition-colors font-accent italic text-lg"
              data-testid="home-programme-details-link"
            >
              Découvrir tous les détails
              <span>→</span>
            </Link>

            <p
              className="mt-8 text-[#e17c3b] font-bold text-lg md:text-xl tracking-wide"
              data-testid="home-dress-code-note"
            >
              Une seule tenue est nécessaire pour l'ensemble du mariage
            </p>
          </div>
        </div>
      </section>

      {/* Hero / Invitation section - now below */}
      <section className="pb-0" data-testid="home-hero-section">
        <div className="flex flex-col lg:flex-row">
          {/* Left Column - Content CENTERED */}
          <div className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-16 py-16 lg:py-20 order-2 lg:order-1 min-h-[60vh] lg:min-h-[80vh]">
            <div className="text-center animate-slide-up">
              {/* Orange decoration */}
              <div className="mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_4ebaa9cc-440b-4527-9761-4d4c07f094d6/artifacts/ok3lmo58_Orange%203.png"
                  alt="Orange decoration"
                  className="h-20 w-auto mx-auto"
                  data-testid="hero-orange-decoration"
                />
              </div>

              <p className="text-sm tracking-[0.3em] uppercase text-[#8d5f3f]/70 mb-4">Nous vous invitons à célébrer</p>

              <h1 className="font-display text-3xl md:text-5xl tracking-tight leading-tight mb-4 text-[#e17c3b]">
                Notre mariage !
              </h1>

              <p className="font-display text-4xl md:text-6xl text-[#8d5f3f] mb-4">Gelly & Christopher</p>

              <p className="text-[#6b8e4e] text-lg mb-2">Séville, Espagne</p>
              <p className="font-accent italic text-xl text-[#8d5f3f]/80 mb-10">18 octobre 2026</p>

              <div>
                <Link to="/mariage">
                  <Button
                    size="lg"
                    className="rounded-full px-10 py-7 text-lg bg-[#e17c3b] hover:bg-[#c96a2f] hover:scale-105 transition-all duration-300 shadow-lg text-white"
                    data-testid="programme-cta-button"
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Programme du mariage
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Right Column - Hero Image */}
          <div className="flex-1 order-1 lg:order-2 relative min-h-[50vh] lg:min-h-[80vh]">
            <img
              src="https://customer-assets.emergentagent.com/job_4ebaa9cc-440b-4527-9761-4d4c07f094d6/artifacts/p1ky32z5_hero-couple.jpg"
              alt="Gelly et Christopher"
              className="absolute inset-0 w-full h-full object-cover object-center md:object-top"
              style={{ objectPosition: 'center 20%' }}
              data-testid="hero-couple-image"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
